import { useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { LanguageToggle, Logo, TranslatedText } from '../ui';

const navLinks = [
  { key: 'whatWeDo', href: '#what-we-do' },
  { key: 'howItWorks', href: '#how-it-works' },
  { key: 'tryouts', href: '#tryouts' },
  { key: 'academies', href: '#academies' },
  { key: 'club', href: '#club' },
  { key: 'scholarships', href: '#soccer-college' },
  { key: 'success', href: '#success-stories' },
  { key: 'partners', href: '#partners' },
  { key: 'pricing', href: '#pricing' },
];

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  
  const { scrollY } = useScroll();
  const headerBg = useTransform(
    scrollY,
    [0, 100],
    ['rgba(10, 22, 40, 0)', 'rgba(10, 22, 40, 0.95)']
  );
  const headerBorder = useTransform(
    scrollY,
    [0, 100],
    ['rgba(255, 215, 0, 0)', 'rgba(255, 215, 0, 0.1)']
  );

  useEffect(() => {
    const handleScroll = () => setHasScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.header
      style={{ backgroundColor: headerBg, borderColor: headerBorder }}
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-all duration-300 ${
        hasScrolled ? 'backdrop-blur-xl' : ''
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <motion.a
            href="#"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            <Logo />
          </motion.a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link, index) => (
              <motion.a
                key={link.key}
                href={link.href}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -2 }}
                className="relative px-4 py-2 text-sm font-medium text-dark-300 hover:text-white transition-colors group"
              >
                <TranslatedText 
                  i18nKey={`nav.${link.key}`}
                  type="scramble"
                  duration={0.4}
                  className="font-medium"
                />
                {/* Underline effect */}
                <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-brand-yellow group-hover:w-1/2 transition-all duration-300" />
                <span className="absolute bottom-0 right-1/2 w-0 h-0.5 bg-brand-yellow group-hover:w-1/2 transition-all duration-300" />
              </motion.a>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-4">
            <LanguageToggle />

            {/* Mobile menu button */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-xl text-dark-300 hover:text-white hover:bg-brand-blue transition-colors"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-brand-navy/95 backdrop-blur-xl border-t border-brand-lightBlue/20"
          >
            <div className="container mx-auto px-4 py-6 space-y-2">
              {navLinks.map((link, index) => (
                <motion.a
                  key={link.key}
                  href={link.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => setIsOpen(false)}
                  className="block px-4 py-3 rounded-xl text-dark-200 hover:text-white hover:bg-brand-blue transition-colors"
                >
                  <TranslatedText 
                    i18nKey={`nav.${link.key}`}
                    type="slide"
                    duration={0.3}
                  />
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
